// Shared display vocabulary for animal processing status.
// Stored/source values intentionally stay as-is (active, processed, scheduled,
// complete) while the UI speaks the WCF planner language.

// The three normalized status keys. Stored values are many and messy (see the
// value sets below); these are the only ones this module ever hands back.
export type ProcessingStatusKey = 'planned' | 'in_process' | 'complete';

// `as const` is a type-only assertion — Object.freeze already provides the
// runtime guarantee. It preserves the literal strings so ProcessingStatusLabel
// below is a real union rather than plain `string`, which is what lets
// processingStatusVariantFromLabel be checked against the labels it compares.
export const PROCESSING_STATUS_DISPLAY = Object.freeze({
  planned: 'Planned',
  inProcess: 'In Process',
  complete: 'Complete',
} as const);

export type ProcessingStatusLabel = (typeof PROCESSING_STATUS_DISPLAY)[keyof typeof PROCESSING_STATUS_DISPLAY];

// Badge tone, not lifecycle state — see processingStatusVariantFromLabel.
export type ProcessingStatusVariant = 'neutral' | 'ok' | 'warn';

// Either a bare stored status string or a record carrying one. Both call shapes
// are in use, so statusFromRecord below discriminates at runtime.
export type ProcessingStatusSource = {status?: unknown} | string | null | undefined;

// Head counts used only to distinguish "In Process" from "Planned" for a pig
// batch. Values arrive from aggregate queries as numbers or numeric strings.
export type PigProcessingMetrics = {
  started?: number | string | null;
  current?: number | string | null;
};

const COMPLETE_VALUES = new Set(['complete', 'completed', 'processed', 'done']);
// 'in-proccess' / 'in proccess' are the REAL Asana "Status (Processing)" option
// spelling (double-c) on the SF Processing Calendar — normalize, don't correct
// the stored value.
const IN_PROCESS_VALUES = new Set([
  'active',
  'in_process',
  'in-process',
  'in process',
  'processing',
  'in-proccess',
  'in proccess',
  'in_proccess',
]);
const PLANNED_VALUES = new Set(['planned', 'scheduled', 'reserved', 'tbc', 'goal']);

export function normalizeProcessingStatus(value: unknown): ProcessingStatusKey {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (COMPLETE_VALUES.has(raw)) return 'complete';
  if (IN_PROCESS_VALUES.has(raw)) return 'in_process';
  if (PLANNED_VALUES.has(raw)) return 'planned';
  return 'planned';
}

export function processingStatusLabel(value: unknown): ProcessingStatusLabel {
  const status = normalizeProcessingStatus(value);
  if (status === 'complete') return PROCESSING_STATUS_DISPLAY.complete;
  if (status === 'in_process') return PROCESSING_STATUS_DISPLAY.inProcess;
  return PROCESSING_STATUS_DISPLAY.planned;
}

export function processingStatusVariantFromLabel(label: string): ProcessingStatusVariant {
  if (label === PROCESSING_STATUS_DISPLAY.complete) return 'neutral';
  if (label === PROCESSING_STATUS_DISPLAY.inProcess) return 'ok';
  return 'warn';
}

function positiveCount(value: unknown): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function statusFromRecord(recordOrStatus: ProcessingStatusSource): unknown {
  return recordOrStatus && typeof recordOrStatus === 'object' ? recordOrStatus.status : recordOrStatus;
}

export function pigBatchProcessingStatusLabel(
  recordOrStatus: ProcessingStatusSource,
  metrics: PigProcessingMetrics = {},
): ProcessingStatusLabel {
  const status = normalizeProcessingStatus(statusFromRecord(recordOrStatus));
  if (status === 'complete') return PROCESSING_STATUS_DISPLAY.complete;
  if (status === 'planned') return PROCESSING_STATUS_DISPLAY.planned;
  if (positiveCount(metrics.started) || positiveCount(metrics.current)) return PROCESSING_STATUS_DISPLAY.inProcess;
  return PROCESSING_STATUS_DISPLAY.planned;
}

export function pigBatchProcessingStatusVariant(
  recordOrStatus: ProcessingStatusSource,
  metrics: PigProcessingMetrics = {},
): ProcessingStatusVariant {
  return processingStatusVariantFromLabel(pigBatchProcessingStatusLabel(recordOrStatus, metrics));
}
